import React, { useEffect, useState } from "react";
import Question from "./Question";
import Results from "./Results";
import { MainPageInfoBox } from "./MainPageInfoBox";
import { convertToDollars, randomlyGetBetOutcome } from "./Question";
import "./App.css";
import "@fontsource/inter/400.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faInfoCircle,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";

import { initializeApp } from "firebase/app";
import { getDatabase, ref, onValue, runTransaction } from "firebase/database";

const firebaseConfig = {
  apiKey: "AIzaSyDGNiWYp2pUWlfxmPOn7cEoC49T4RXmYxI",
  authDomain: "risky-bets.firebaseapp.com",
  projectId: "risky-bets",
  storageBucket: "risky-bets.firebasestorage.app",
  messagingSenderId: "849626342316",
  appId: "1:849626342316:web:8b5f149133d9d7067b5009",
  measurementId: "G-EKHTHT9KC2",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// enable dev button and total earnings
const ENABLE_DEV_TOOLS = false;

// utility function
// represented in sixths
// low risk: 1/3
// medium risk: 1/2
// high risk: 2/3
// super high risk: 1 (equal risk)
// overly high: 4/3
export const RISK_LEVEL = {
  // LOW: 2,
  MEDIUM: 3,
  // HIGH: 4,
  // SUPER_HIGH: 6,
  TOO_HIGH: 8,
};

// represents the average price point of the game
export const PRICE_POINT = {
  ONE: 1,
  TEN: 10,
  HUNDRED: 100,
  // THOUSAND: 1000,
  TEN_THOUSAND: 10000,
  // HUNDRED_THOUSAND: 100000,
  MILLION: 1000000,
  // TEN_MILLION: 10000000,
};

// whether the game is gain or loss
export const GAIN_LOSS = {
  GAIN: 0,
  LOSS: 1,
};

export const SAFE_OR_GAMBLE = {
  SAFE: 0,
  GAMBLE: 1,
};

// constants that control things
// base amount is multiplied by random amount between minBaseJitter and maxBaseJitter
export const minBaseJitter = 0.5;
export const maxBaseJitter = 1.5;
// bet percent is random between minBetPercent and maxBetPercent
export const minBetPercent = 0.0;
export const maxBetPercent = 1.0;

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    // Pick a random index from 0 to i
    const randomIndex = Math.floor(Math.random() * (i + 1));

    // Swap elements at i and randomIndex
    [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
  }
  return array;
}

function generateInitialAnswers() {
  const answers = {};
  Object.values(GAIN_LOSS).forEach((gainLoss) => {
    answers[gainLoss] = {};
    Object.values(PRICE_POINT).forEach((pricePoint) => {
      answers[gainLoss][pricePoint] = {};
      Object.values(RISK_LEVEL).forEach((riskLevel) => {
        answers[gainLoss][pricePoint][riskLevel] = null;
      });
    });
  });
  return answers;
}

const App = () => {
  const [clickCount, setClickCount] = useState(0);
  const [answers, setAnswers] = useState(generateInitialAnswers);
  const [betTotal, setBetTotal] = useState(0);
  const [ephemeralNumber, setEphemeralNumber] = useState(null);
  const [flashScreen, setFlashScreen] = useState(false);
  const nonNullKeys = [];
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const countRef = ref(database, "buttonPressCount");
    onValue(countRef, (snapshot) => {
      setClickCount(snapshot.val() || 0);
    });
  }, []);

  Object.values(GAIN_LOSS).forEach((gainLoss) => {
    Object.values(PRICE_POINT).forEach((pricePoint) => {
      Object.values(RISK_LEVEL).forEach((riskLevel) => {
        nonNullKeys.push({ gainLoss, pricePoint, riskLevel });
      });
    });
  });

  const [randomizedKeys] = useState(() => shuffleArray(nonNullKeys));
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  async function handleChoice(
    choice,
    gainLoss,
    pricePoint,
    riskLevel,
    betOutcome
  ) {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [gainLoss]: {
        ...prevAnswers[gainLoss],
        [pricePoint]: {
          ...prevAnswers[gainLoss][pricePoint],
          [riskLevel]: choice,
        },
      },
    }));

    setEphemeralNumber(betOutcome);
    setBetTotal((prevTotal) => prevTotal + betOutcome);
    setFlashScreen(true); // Trigger screen flash
    setTimeout(() => {
      setEphemeralNumber(null);
      setFlashScreen(false); // End screen flash
    }, 1800);

    if (currentQuestionIndex < randomizedKeys.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // End of quiz
      setCurrentQuestionIndex(null);
    }

    // update total number of bets placed
    const countRef = ref(database, "buttonPressCount");
    runTransaction(countRef, (newCount) => {
      return (newCount || 0) + 1;
    });
  }

  function devClickThroughAll() {
    let index = 0;

    function clickNext() {
      if (index < randomizedKeys.length) {
        const { gainLoss, pricePoint, riskLevel } = randomizedKeys[index];
        const randomChoice =
          Math.random() < 0.5 ? SAFE_OR_GAMBLE.SAFE : SAFE_OR_GAMBLE.GAMBLE;

        const betOutcome = randomlyGetBetOutcome(
          randomChoice,
          gainLoss,
          pricePoint,
          riskLevel
        );

        handleChoice(randomChoice, gainLoss, pricePoint, riskLevel, betOutcome);

        index += 1;
        setTimeout(clickNext, 200);
      } else {
        setCurrentQuestionIndex(null);
      }
    }

    clickNext();
  }

  function getFontSize(number) {
    if (number == "0") {
      return `4rem`;
    }
    const numDigits = Math.max(
      1,
      Math.abs(Math.floor(number)).toString().length
    );
    return `${numDigits * 2 - 1}rem`;
  }

  return (
    <>
      <div
        className={`flash-overlay ${flashScreen ? "flash" : ""} ${
          ephemeralNumber === 0
            ? "even"
            : ephemeralNumber > 0
            ? "positive"
            : "negative"
        }`}
      />
      <div className={`gradient-background`} />
      <div className={`app-container `}>
        {currentQuestionIndex !== null ? (
          <>
            <div className="info-button">
              <FontAwesomeIcon
                icon={faInfoCircle}
                size={window.innerWidth < 768 ? "2x" : "3x"}
                onClick={() => setShowPopup(true)}
                style={{ cursor: "pointer" }}
              />
            </div>
            <h1 className="page-header">Would You Rather</h1>
            <Question
              onChoice={(choice, gainLoss, pricePoint, riskLevel, betOutcome) =>
                handleChoice(
                  choice,
                  gainLoss,
                  pricePoint,
                  riskLevel,
                  betOutcome
                )
              }
              gainLoss={randomizedKeys[currentQuestionIndex].gainLoss}
              pricePoint={randomizedKeys[currentQuestionIndex].pricePoint}
              riskLevel={randomizedKeys[currentQuestionIndex].riskLevel}
            />
            {ENABLE_DEV_TOOLS ? (
              <>
                <button
                  onClick={devClickThroughAll}
                  style={{ marginTop: "20px" }}
                >
                  Skip through quiz (DEV)
                </button>
                <div>{convertToDollars(betTotal)}</div>
              </>
            ) : null}
            <div>
              {showPopup && (
                <MainPageInfoBox onClose={() => setShowPopup(false)} />
              )}
            </div>
          </>
        ) : (
          <Results answers={answers} betTotal={betTotal} database={database} />
        )}
        {ephemeralNumber !== null && (
          <div
            className={`sliding-number ${
              ephemeralNumber === 0
                ? "even"
                : ephemeralNumber > 0
                ? "positive"
                : "negative"
            }`}
            style={{ fontSize: getFontSize(ephemeralNumber) }}
          >
            {ephemeralNumber < 0 ? "" : "+"}
            {convertToDollars(ephemeralNumber)}
          </div>
        )}
      </div>
      {currentQuestionIndex == 0 || currentQuestionIndex === null ? null : (
        <div className={`earnings locked`}>
          <p>
            Total winnings:&nbsp;
            <FontAwesomeIcon icon={faLock} size="lg" />
          </p>
        </div>
      )}
      {currentQuestionIndex !== null ? (
        <p className="questions-remaining">
          {randomizedKeys.length - currentQuestionIndex} question
          {randomizedKeys.length - currentQuestionIndex > 1 ? "s" : ""} until
          results
        </p>
      ) : null}
      <div className="refresh-button" onClick={() => window.location.reload()}>
        <FontAwesomeIcon icon={faRefresh} size="3x" />
      </div>
      <div className={"logo-container"}>
        <a href="https://johngrandolph.com">
          <img src="./logo3.png" alt="logo" className={"logo"} />
        </a>
      </div>
      <div className="button-click-count">
        {clickCount.toLocaleString()} bets placed
      </div>
    </>
  );
};

export default App;
